import { abbrFromLabel, colorFromId } from '@/lib/catalog/display';
import { type CatalogApp, type CatalogTileV1, ENVIRONMENTS, type Environment, type LaunchProtocol } from '@/types/catalog';

const SECTIONS = new Set(['tools', 'environments']);
const PROTOCOLS = new Set<LaunchProtocol>(['saml', 'oidc', 'url']);
const ENVIRONMENT_SET = new Set<Environment>(ENVIRONMENTS);

/** Hard-coded Security tile — not published to SSM. */
export const securityTile = (): CatalogApp => ({
  id: 'security-google-2fa',
  clientId: 'security-google-2fa',
  section: 'tools',
  label: 'Google Account security',
  description: 'Manage 2FA and account security',
  abbr: '2F',
  color: '#4285F4',
  icon: '/icons/google.svg',
  launchUrl: 'https://myaccount.google.com/security',
  protocol: 'url',
  entitlementPrefix: '',
  sortOrder: 1000,
  alwaysVisible: true,
});

type EnvLink = { environment: Environment; launchUrl: string; description: string };

const envLinkTiles = (slug: string, meta: { label: string; abbr: string; color: string; icon: string; sortBase: number }, links: EnvLink[]): CatalogApp[] =>
  links.map(({ environment, launchUrl, description }, index) => ({
    id: `${slug}-${environment}`,
    clientId: `${slug}-${environment}`,
    section: 'environments' as const,
    label: meta.label,
    description,
    abbr: meta.abbr,
    color: meta.color,
    icon: meta.icon,
    launchUrl,
    protocol: 'url' as const,
    entitlementPrefix: '',
    sortOrder: meta.sortBase + index,
    environment,
    alwaysVisible: true,
  }));

const EXCHANGE_LINKS: EnvLink[] = [
  { environment: 'dev', launchUrl: 'https://tokyo-v2.dev.tradingone.io/', description: 'Exchange trading UI (dev)' },
  { environment: 'staging', launchUrl: 'https://tokyo.tradingone.io/BTC_USDT', description: 'Exchange trading UI (staging)' },
  { environment: 'preprod', launchUrl: 'https://tokyo.preprod.tradingone.io/ETH_EUR', description: 'Exchange trading UI (preprod)' },
  { environment: 'production', launchUrl: 'https://fast.exchange.onetrading.com/', description: 'Exchange trading UI (production)' },
];

/** Hard-coded Exchange environment tiles — not published to SSM. */
export const exchangeTiles = (): CatalogApp[] => envLinkTiles('exchange', { label: 'Exchange', abbr: 'EX', color: '#1F49FF', icon: '/favicon.svg', sortBase: 100 }, EXCHANGE_LINKS);

const urlTile = (id: string, label: string, description: string, launchUrl: string, icon: string, abbr: string, color: string, sortOrder: number): CatalogApp => ({
  id,
  clientId: id,
  section: 'tools',
  label,
  description,
  abbr,
  color,
  icon,
  launchUrl,
  protocol: 'url',
  entitlementPrefix: '',
  sortOrder,
  alwaysVisible: true,
});

/** Hard-coded Google Workspace tiles — not published to SSM. */
export const googleWorkspaceTiles = (): CatalogApp[] => [
  urlTile('google-gmail', 'Gmail', 'Send and receive email', 'https://mail.google.com/', '/icons/gmail.svg', 'GM', '#EA4335', 1001),
  urlTile('google-calendar', 'Google Calendar', 'View and manage your calendar', 'https://calendar.google.com/', '/icons/google-calendar.svg', 'GC', '#4285F4', 1002),
  urlTile('google-drive', 'Google Drive', 'Files and shared drives', 'https://drive.google.com/', '/icons/google-drive.svg', 'GD', '#0F9D58', 1003),
];

/** Hard-coded OpenPanel tile — self-hosted analytics, not published to SSM. */
export const openPanelTile = (): CatalogApp =>
  urlTile('openpanel', 'OpenPanel', 'Web and product analytics', 'https://metrics.fast.onetrading.com/onetrading', '/icons/openpanel.svg', 'OP', '#2564EB', 1004);

const isTileV1 = (value: unknown): value is CatalogTileV1 => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const tile = value as Record<string, unknown>;
  return (
    typeof tile.id === 'string' &&
    typeof tile.clientId === 'string' &&
    typeof tile.label === 'string' &&
    typeof tile.description === 'string' &&
    typeof tile.launchUrl === 'string' &&
    typeof tile.entitlementPrefix === 'string' &&
    typeof tile.sortOrder === 'number' &&
    typeof tile.section === 'string' &&
    SECTIONS.has(tile.section) &&
    typeof tile.protocol === 'string' &&
    PROTOCOLS.has(tile.protocol as LaunchProtocol) &&
    (tile.environment === undefined || (typeof tile.environment === 'string' && ENVIRONMENT_SET.has(tile.environment as Environment))) &&
    (tile.icon === undefined || typeof tile.icon === 'string')
  );
};

/** Hosted SVGs for catalog tiles that omit `icon` (Keycloak admin consoles). */
const HOSTED_ICONS: Readonly<Record<string, string>> = {
  'keycloak-internal': '/icons/keycloak-internal.svg',
  'keycloak-broker': '/icons/keycloak-broker.svg',
  'keycloak-customer': '/icons/keycloak-customer.svg',
};

const hostedIconFor = (id: string): string | undefined => {
  if (id === 'keycloak-internal' || id.startsWith('keycloak-internal-')) {
    return HOSTED_ICONS['keycloak-internal'];
  }
  if (id === 'keycloak-broker' || id.startsWith('keycloak-broker-')) {
    return HOSTED_ICONS['keycloak-broker'];
  }
  if (id === 'keycloak-customer' || id.startsWith('keycloak-customer-')) {
    return HOSTED_ICONS['keycloak-customer'];
  }
  return undefined;
};

export const toCatalogApp = (tile: CatalogTileV1): CatalogApp => ({
  ...tile,
  icon: tile.icon ?? hostedIconFor(tile.id),
  abbr: abbrFromLabel(tile.label),
  color: colorFromId(tile.id),
});

/**
 * SSM publishes one tools tile per Keycloak admin console (production URL).
 * Expand into Environments: Internal is dev+production; Broker/Customer are all four.
 */
const KEYCLOAK_ENV_URLS: Readonly<Record<string, Partial<Record<Environment, string>>>> = {
  'keycloak-internal': {
    dev: 'https://tokyo-keycloak-internal.dev.tradingone.io/admin/master/console/',
    production: 'https://keycloak-internal.fast.onetrading.com/admin/master/console/',
  },
  'keycloak-broker': {
    dev: 'https://tokyo-keycloak-broker.dev.tradingone.io/admin/master/console/',
    staging: 'https://tokyo-keycloak-broker.tradingone.io/admin/master/console/',
    preprod: 'https://tokyo-keycloak-broker.preprod.tradingone.io/admin/master/console/',
    production: 'https://keycloak-broker.fast.onetrading.com/admin/master/console/',
  },
  'keycloak-customer': {
    dev: 'https://tokyo-keycloak-customer.dev.tradingone.io/admin/master/console/',
    staging: 'https://tokyo-keycloak-customer.tradingone.io/admin/master/console/',
    preprod: 'https://tokyo-keycloak-customer.preprod.tradingone.io/admin/master/console/',
    production: 'https://keycloak-customer.fast.onetrading.com/admin/master/console/',
  },
};

const KEYCLOAK_ENVS: Readonly<Record<string, readonly Environment[]>> = {
  'keycloak-internal': ['dev', 'production'],
  'keycloak-broker': ENVIRONMENTS,
  'keycloak-customer': ENVIRONMENTS,
};

export const expandKeycloakEnvTiles = (apps: readonly CatalogApp[]): CatalogApp[] => {
  const existingIds = new Set(apps.map((app) => app.id));
  const expanded: CatalogApp[] = [];

  for (const app of apps) {
    const environments = KEYCLOAK_ENVS[app.id];
    if (!environments || app.environment) {
      expanded.push(hostedIconFor(app.id) ? { ...app, requiresAdminAccess: true } : app);
      continue;
    }

    const urls = KEYCLOAK_ENV_URLS[app.id] ?? {};
    for (const [index, environment] of environments.entries()) {
      const id = `${app.id}-${environment}`;
      if (existingIds.has(id)) {
        continue;
      }
      const launchUrl = environment === 'production' ? app.launchUrl : (urls[environment] ?? app.launchUrl);
      expanded.push({
        ...app,
        id,
        clientId: id,
        section: 'environments',
        launchUrl,
        environment,
        sortOrder: app.sortOrder * 10 + index,
        color: colorFromId(app.id),
        icon: app.icon ?? hostedIconFor(app.id),
        requiresAdminAccess: true,
      });
    }
  }

  return expanded;
};

/** Local-only tiles appended after the baked SSM catalog (Google Workspace, OpenPanel, Exchange). */
export const hardcodedTiles = (): CatalogApp[] => [securityTile(), ...googleWorkspaceTiles(), openPanelTile(), ...exchangeTiles()];

export const parseCatalogTiles = (raw: unknown): CatalogApp[] => {
  if (!Array.isArray(raw)) {
    throw new Error('app-catalog.json must be a JSON array');
  }
  const apps = expandKeycloakEnvTiles(raw.filter(isTileV1).map(toCatalogApp));
  if (apps.length === 0) {
    throw new Error('app-catalog.json contains no valid schema v1 tiles');
  }
  return apps;
};

export const sortCatalog = (apps: CatalogApp[]): CatalogApp[] =>
  [...apps].sort((a, b) => {
    const sectionOrder = a.section === b.section ? 0 : a.section === 'tools' ? -1 : 1;
    if (sectionOrder !== 0) {
      return sectionOrder;
    }
    const byLabel = a.label.localeCompare(b.label);
    if (byLabel !== 0) {
      return byLabel;
    }
    const envA = a.environment ? ENVIRONMENTS.indexOf(a.environment) : -1;
    const envB = b.environment ? ENVIRONMENTS.indexOf(b.environment) : -1;
    return envA - envB;
  });

/** Load baked SSM catalog from the deployed static asset and append hard-coded tiles. */
export const loadBakedCatalog = async (signal?: AbortSignal): Promise<CatalogApp[]> => {
  const response = await fetch('/app-catalog.json', {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to load app-catalog.json (${response.status}). Run bun run catalog:fetch before local dev/build.`);
  }
  const apps = parseCatalogTiles(await response.json());
  return sortCatalog([...apps, ...hardcodedTiles()]);
};
